"use client";

import { useState, useCallback, useEffect, useRef } from "react";
import { useMembership } from "@/lib/hooks/useMembership";

import { useMySignups } from "@/lib/hooks/useMySignups";
import { expandToOccurrences, formatDateParam, type OccurrenceEntry } from "@/lib/utils/recurrence";
import { getUrlFromLinkObject } from "@/lib/storyblok/utils";
import { storyblokEditable } from "@storyblok/react/rsc";
import Autoplay from "embla-carousel-autoplay";
import { useLightboxOpen } from "@/components/layout/LightboxProvider";

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  GalleryNavPrevious,
  GalleryNavNext,
  type CarouselApi,
} from "@/components/ui/carousel";
import { EventCard } from "@/components/layout/EventCard";
import { GridContainer } from "@/components/layout/GridContainer";
import { EventPreviewModal, type EventPreviewData } from "@/components/landing/EventPreviewModal";
import { EventSignupModal, type EventSignupData } from "@/components/events/EventSignupModal";
import { useIsLandingPage } from "@/lib/landing/LandingContext";
import { trackEvent } from "@/lib/analytics";
import type { EventStoryblok } from "@/component-types-sb";
import type { MaskShapeVariant } from "@/components/illustrations/MaskShape";
import { eventTypeFromContent } from "@/lib/utils/eventType";
import {
  fetchStoryblokList,
  fetchEventsAndTravels,
} from "@/lib/storyblok/fetchList";

// Background color options for the gallery
type GalleryBackgroundColor = "none" | "white" | "sand-100" | "sky-50";

const backgroundColorClasses: Record<GalleryBackgroundColor, string> = {
  none: "bg-transparent",
  white: "bg-white",
  "sand-100": "bg-sand-100",
  "sky-50": "bg-sky-50",
};

// Text colors based on background
const textColorClasses: Record<GalleryBackgroundColor, string> = {
  none: "text-black",
  white: "text-black",
  "sand-100": "text-black",
  "sky-50": "text-black",
};

// Inline type definition until Storyblok types are regenerated
type EventGalleryStoryblok = {
  title?: string;
  count?: number;
  category?: string | string[];
  tags?: (number | string)[];
  tag_mode?: "or" | "and";
  member_access?: "" | "all" | "public" | "member_only";
  background_color?: GalleryBackgroundColor;
  _uid: string;
  component: "event_gallery";
  [k: string]: any;
};

type EventGalleryProps = {
  blok: EventGalleryStoryblok;
};

type EventData = {
  uuid: string;
  title: string;
  date: Date;
  originalDate: Date;
  endDate?: Date;
  eventType: string;
  image?: { src: string; alt: string };
  maskShape: MaskShapeVariant;
  ctaLink: string;
  memberOnly: boolean;
  /** Fuer das Anmelde-Modal: landet bei der Anmeldung in der Datenbank. */
  slug: string;
  /** Whereby-Zugang, geht in die Bestaetigungsmail. */
  eventLink?: string;
  description?: any;
  experts?: { name: string; image?: { filename: string; alt?: string } }[];
};


const EventGallery = ({ blok }: EventGalleryProps) => {
  const isLanding = useIsLandingPage();
  const { isMember } = useMembership();
  const { isSignedUp } = useMySignups();
  const [events, setEvents] = useState<EventData[]>([])
  const [isLoading, setIsLoading] = useState(true);
  const [previewEvent, setPreviewEvent] = useState<EventPreviewData | null>(null);
  const [signupEvent, setSignupEvent] = useState<EventSignupData | null>(null);
  const [api, setApi] = useState<CarouselApi>();
  const [canScroll, setCanScroll] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const lightboxOpen = useLightboxOpen();
  const autoplayRef = useRef(
    Autoplay({
      delay: 3000,
      stopOnInteraction: true,
      stopOnMouseEnter: true,
    }),
  );

  const count = blok.count || 3;
  // Normalize category: Storyblok multi-option returns array, single-option returns string
  const rawCategory = blok.category || "all";
  const category = Array.isArray(rawCategory) ? rawCategory.join(",") : rawCategory;
  const tags = blok.tags;
  const tagMode = blok.tag_mode || "or";
  const accessFilter = blok.member_access || "all";
  const bgColor = blok.background_color || "none";
  const bgClass = backgroundColorClasses[bgColor];
  const textClass = textColorClasses[bgColor];

  // Fetch events on mount
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        // Reisen sind ein eigener Content-Type, sollen in Galerien aber neben
        // Events erscheinen — geladen wird beides und dann vereinigt.
        const filterQuery: Record<string, any> = {};

        // `category` gibt es nur an Events; an Reisen waere der Filter leer
        // und wuerde sie faelschlich aussortieren.
        const eventOnlyFilter: Record<string, any> | undefined =
          category && category !== "all"
            ? { category: { in: category } }
            : undefined;

        // Eine echte Kategorie (diaTalk, community, ...) gibt es nur an
        // Events. Weil der Kategoriefilter nur auf die event-Haelfte wirkt,
        // liefe die travel-Haelfte ungefiltert mit — eine DiaTalk-Galerie
        // bekaeme alle Reisen dazu. Also nur bei "alle" und "reisen" beide
        // Typen laden.
        const wantsTravel = !category || category === "all" || category === "reisen";

        // If tags are provided, filter by them
        if (tags && tags.length > 0) {
          filterQuery.tags = { any_in_array: tags.map(String).join(",") };
        }

        // Filter by access level
        if (accessFilter === "member_only") {
          filterQuery.member_access = { in: "member_only" };
        } else if (accessFilter === "public") {
          filterQuery.member_access = { not_in: "member_only" };
        }

        const shared = {
          // Absteigend sortieren und grosszuegig holen.
          //
          // Aufsteigend lieferte Storyblok die AELTESTEN Storys — und
          // expandToOccurrences wirft unten alles Vergangene weg. Gemessen am
          // 19.09.2026: von den 20 so geholten Events lag KEINES in der
          // Zukunft. Die Galerie lief praktisch leer; das ist ein
          // Bestandsfehler, kein Nebeneffekt der Reise-Umstellung.
          //
          // Warum kein `date: { gt_date: ... }`-Filter, der naheliegend
          // waere: 20 Serientermine tragen ein vergangenes `date` und haben
          // ihre kuenftigen Termine nur in `repeating_dates` (z.B.
          // "mylife-loop", date=22.04.2026, naechster Termin 21.10.2026).
          // Ein Datumsfilter loeschte genau diese aus der Galerie — das
          // gesamte wiederkehrende Programm.
          sort_by: "content.date:desc" as const,
          per_page: 100,
          // Bewusst KEIN `exact`: `per_page` ist hier ein Kandidatenpool, aus
          // dem expandToOccurrences unten die kuenftigen Termine zieht.
          filter_query: filterQuery,
          // Der Helfer uebersetzt das fuer die travel-Haelfte selbst zu
          // `travel.experts` — pro Content-Type heisst die Relation anders.
          ...(isLanding && { resolve_relations: "event.experts" }),
        };

        const stories = wantsTravel
          ? await fetchEventsAndTravels({ ...shared, eventOnlyFilter })
          : await fetchStoryblokList({
              ...shared,
              content_type: "event",
              filter_query: { ...filterQuery, ...eventOnlyFilter },
            });

        if (stories && stories.length > 0) {
          let filtered = stories;

          if (tagMode === "and" && tags && tags.length > 1) {
            const requiredTags = new Set(tags.map(String));
            filtered = filtered.filter((story: any) => {
              const storyTags = ((story.content as EventStoryblok).tags || []).map(String);
              return [...requiredTags].every((t) => storyTags.includes(t));
            });
          }

          const occurrences = filtered.flatMap(expandToOccurrences);
          occurrences.sort(
            (a: OccurrenceEntry, b: OccurrenceEntry) => a.displayDate.getTime() - b.displayDate.getTime(),
          );

          const fetchedEvents = occurrences.slice(0, count).map((occ: OccurrenceEntry) => {
            const content = occ.story.content as EventStoryblok;
            return {
              uuid: occ.story.uuid,
              title: content.title,
              date: occ.displayDate,
              originalDate: occ.originalDate,
              endDate: occ.endDate,
              eventType: eventTypeFromContent(content),
              image: content.image?.filename
                ? {
                    src: content.image.filename,
                    alt: content.image.alt || content.title,
                  }
                : undefined,
              maskShape: (content.image_mask_shape ||
                "four_vertical_even") as MaskShapeVariant,
              ctaLink: occ.isRepeating
                ? `/${occ.story.full_slug}?date=${formatDateParam(occ.originalDate)}`
                : `/${occ.story.full_slug}`,
              memberOnly: content.member_access === "member_only",
              // Fuer das Anmelde-Modal: Slug und Whereby-Link landen bei der
              // Anmeldung in der Datenbank, der Link geht zusaetzlich in die
              // Bestaetigungsmail.
              slug: `/${occ.story.full_slug}`,
              eventLink: getUrlFromLinkObject(content.link) || undefined,
              description: content.text,
              experts: isLanding
                ? ((content as any).experts || [])
                    .filter((e: any) => typeof e !== "string" && e?.content)
                    .map((e: any) => ({
                      name: e.content.name,
                      image: e.content.image,
                    }))
                : undefined,
            };
          });
          setEvents(fetchedEvents);
        }
      } catch (error) {
        console.error("Error fetching events:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEvents();
  }, [count, category, tags, tagMode, accessFilter]);

  const eventCount = events.length;

  // Check if carousel can scroll (i.e., content overflows)
  const checkCanScroll = useCallback(() => {
    if (!api) return;
    const canScrollNext = api.canScrollNext();
    const canScrollPrev = api.canScrollPrev();
    setCanScroll(canScrollNext || canScrollPrev);
  }, [api]);

  useEffect(() => {
    if (!api) return;

    // Initial check
    checkCanScroll();

    // Re-check on resize
    api.on("resize", checkCanScroll);
    api.on("reInit", checkCanScroll);

    return () => {
      api.off("resize", checkCanScroll);
      api.off("reInit", checkCanScroll);
    };
  }, [api, checkCanScroll]);

  // Track viewport visibility
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsInView(entry.isIntersecting),
      { threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Control autoplay based on canScroll and viewport visibility
  useEffect(() => {
    const autoplay = autoplayRef.current;
    if (!api) return;

    if (canScroll && isInView && !lightboxOpen) {
      autoplay.play();
    } else {
      autoplay.stop();
    }
  }, [api, canScroll, isInView, lightboxOpen]);

  // Show loading state
  if (isLoading) {
    return (
      <div {...storyblokEditable(blok)} className="w-full">
        <section
          className={`${bgClass} py-8 flex flex-col gap-4 w-full items-center rounded-3xl overflow-hidden`}
        >
          <h2 className={`text-headline-xl ${textClass} text-center mb-2`}>
            {blok.title || "Aktuelle Events"}
          </h2>
          <div className="flex gap-4 px-4 md:px-8">
            {[...Array(Math.min(count, 3))].map((_, i) => (
              <div
                key={i}
                className="w-[278px] h-[350px] bg-sand-100 rounded-2xl animate-pulse"
              />
            ))}
          </div>
        </section>
      </div>
    );
  }

  if (eventCount === 0) {
    return (
      <div {...storyblokEditable(blok)} className="w-full">
        <section
          className={`${bgClass} py-8 flex flex-col gap-4 w-full items-center rounded-3xl overflow-hidden`}
        >
          <h2 className={`text-headline-xl ${textClass} text-center mb-2`}>
            {blok.title || "Aktuelle Events"}
          </h2>
          <p className={`text-copy text-center ${textClass} opacity-60`}>
            Keine Events gefunden
          </p>
        </section>
      </div>
    );
  }

  return (
    <div ref={sectionRef} {...storyblokEditable(blok)} className="w-full">
      <section
        className={`${bgClass} py-8 flex flex-col gap-4 w-full items-center rounded-3xl overflow-hidden`}
      >
        {/* Header */}
        <h2 className={`text-headline-xl ${textClass} text-center mb-2`}>
          {blok.title || "Aktuelle Events"}
        </h2>

        {/* Event Cards Carousel */}
        <GridContainer className="!px-0">
          <div className="col-span-12 lg:col-span-10 lg:col-start-2">
            <Carousel
              setApi={setApi}
              opts={{
                align: "center",
                loop: false,
                dragFree: false,
                watchResize: true,
                breakpoints: {
                  "(min-width: 768px)": {
                    align: "start",
                    dragFree: true,
                  },
                },
              }}
              plugins={[autoplayRef.current]}
              className="w-full relative"
            >
              <GalleryNavPrevious show={canScroll} />
              <CarouselContent
                className={
                  canScroll
                    ? "ml-0 pl-4 md:pl-8 lg:pl-0 items-stretch"
                    : "justify-center items-stretch"
                }
              >
                {events.map((event, index) => (
                  <CarouselItem
                    key={`${event.ctaLink}-${index}`}
                    className={
                      canScroll
                        ? "pl-4 first:lg:pl-0 basis-auto h-auto card-rotate-container"
                        : "basis-auto px-2 h-auto card-rotate-container"
                    }
                  >
                    <EventCard
                      eventType={event.eventType}
                      date={event.date}
                      endDate={event.endDate}
                      title={event.title}
                      image={event.image}
                      maskShape={event.maskShape}
                      ctaLink={event.ctaLink}
                      memberOnly={event.memberOnly}
                      isMember={isMember}
                      className="h-full"
                      signedUp={isSignedUp(event.uuid, event.originalDate.toISOString())}
                      onSignupClick={() => setSignupEvent({
                        storyblokEventUuid: event.uuid,
                        eventDate: event.originalDate.toISOString(),
                        eventDisplayDate: event.date.toISOString(),
                        eventTitle: event.title,
                        eventSlug: event.slug,
                        returnUrl: event.ctaLink,
                        eventType: event.eventType,
                        eventLink: event.eventLink,
                        memberOnly: event.memberOnly,
                      })}
                      onPreviewClick={isLanding ? () => {
                        setPreviewEvent({
                          title: event.title,
                          date: event.date,
                          endDate: event.endDate,
                          eventType: event.eventType,
                          image: event.image,
                          memberOnly: event.memberOnly,
                          description: event.description,
                          experts: event.experts,
                        });
                        trackEvent("content_preview_opened", {
                          type: "event",
                          title: event.title,
                          source: "event_gallery",
                          eventType: event.eventType,
                        });
                      } : undefined}
                    />
                  </CarouselItem>
                ))}
                {/* Spacer for right padding */}
                {canScroll && <div className="shrink-0 w-4 md:w-8 lg:w-0" />}
              </CarouselContent>
              <GalleryNavNext show={canScroll} />
            </Carousel>
          </div>
        </GridContainer>
      </section>
      {previewEvent && (
        <EventPreviewModal event={previewEvent} onClose={() => setPreviewEvent(null)} />
      )}

      {/* Anmeldung direkt aus der Karte. Dasselbe Modal wie auf der
          Event-Seite — es entscheidet selbst, ob es die Anmeldung zeigt, zum
          Login fuehrt oder zur Mitgliedschaft. */}
      {signupEvent && (
        <EventSignupModal
          open={!!signupEvent}
          onOpenChange={(open) => !open && setSignupEvent(null)}
          event={signupEvent}
        />
      )}
    </div>
  );
};

export default EventGallery;
